import React, { useState } from "react";
import ViewAndEditUploadedWorkspaceFile from "./ViewAndEditUploadedWorkspaceFile";
import "../../css/UploadDocument.css";
import "../../css/ImportDocument.css";

function UploadWorkspaceDocument({
    onBack,
    onNext,
    workspaceId,
    enhancedRightClickEnabled,
    showContextMenu,
    ContextMenuComponent
}) {
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [documentTitle, setDocumentTitle] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const [imageAttachments, setImageAttachments] = useState([]);
    const [showViewAndEdit, setShowViewAndEdit] = useState(false);

    const handleBackClick = () => {
        if (onBack) {
            setIsTransitioning(true);
            setTimeout(() => {
                onBack();
            }, 150);
        }
    };

    const handleNextClick = () => {
        // Validate document title before proceeding
        if (!documentTitle || documentTitle.trim() === "") {
            alert("Please enter a document title.");
            return;
        }

        // Check that title only contains letters, numbers, spaces, periods, dashes, and underscores
        if (!/^[a-zA-Z0-9\s\.\-\_]+$/.test(documentTitle)) {
            alert(
                "Document title must contain only letters, numbers, spaces, periods, dashes, and underscores."
            );
            return;
        }

        if (!selectedFile) {
            alert("Please select an HTML or Markdown file to upload.");
            return;
        }

        setIsTransitioning(true);
        setTimeout(() => {
            setShowViewAndEdit(true);
        }, 150);
    };

    const handleBackFromViewAndEdit = () => {
        setShowViewAndEdit(false);
        setIsTransitioning(false);
    };

    if (showViewAndEdit) {
        return (
            <ViewAndEditUploadedWorkspaceFile
                onBack={handleBackFromViewAndEdit}
                documentTitle={documentTitle}
                selectedFile={selectedFile}
                imageAttachments={imageAttachments}
                workspaceId={workspaceId}
                enhancedRightClickEnabled={enhancedRightClickEnabled}
                showContextMenu={showContextMenu}
                ContextMenuComponent={ContextMenuComponent}
            />
        );
    }

    const isSupportedImportFile = (file) => {
        return file.type === "text/html" ||
            file.type === "text/markdown" ||
            /\.(html|md|markdown|mdown|mkd)$/i.test(file.name);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && isSupportedImportFile(file)) {
            setSelectedFile(file);
        } else {
            alert("Please select a valid HTML or Markdown file.");
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const files = e.dataTransfer.files;
        if (files.length > 0) {
            const file = files[0];
            if (isSupportedImportFile(file)) {
                setSelectedFile(file);
            } else {
                alert("Please select a valid HTML or Markdown file.");
            }
        }
    };

    const handleImageUpload = (e) => {
        const files = Array.from(e.target.files);
        const validImages = files.filter(file => file.type.startsWith('image/'));
        
        if (validImages.length !== files.length) {
            alert("Some files were not images and have been excluded.");
        }
        
        setImageAttachments(prev => [...prev, ...validImages]);
    };

    const removeImageAttachment = (index) => {
        setImageAttachments(prev => prev.filter((_, i) => i !== index));
    };

    const maxTitleLength = 150;

    return (
        <div 
            className={`ImportDocument__main-div ${isTransitioning ? 'transitioning' : ''}`}
            onContextMenu={
                enhancedRightClickEnabled
                    ? (e) => {
                        e.preventDefault();
                        showContextMenu(e.clientX, e.clientY);
                    }
                    : undefined
            }
        >
            <div className="ImportDocument__content">
                <h2>Upload Your Document to Workspace</h2>

                <p className="medium" style={{ marginTop: "20px" }}>
                    Please provide a title for your document and select the HTML or Markdown (.md) file you want to import into your workspace.
                </p>

                {/* Document title input */}
                <div style={{ marginTop: "30px" }}>
                    <label style={{ 
                        display: "block", 
                        marginBottom: "8px", 
                        fontWeight: "bold",
                        fontSize: "16px"
                    }}>
                        Document Title:
                    </label>
                    <input
                        type="text"
                        value={documentTitle}
                        onChange={(e) => setDocumentTitle(e.target.value)}
                        placeholder="Enter document title..."
                        maxLength={maxTitleLength}
                        style={{
                            width: "100%",
                            padding: "12px",
                            fontSize: "16px",
                            border: "2px solid var(--bb-text)",
                            borderRadius: "6px",
                            backgroundColor: "rgba(255, 255, 255, 0.9)",
                            color: "var(--bb-text)",
                            boxSizing: "border-box"
                        }}
                        required
                    />
                    <p style={{ 
                        fontSize: "12px", 
                        color: "var(--bb-text)", 
                        marginTop: "5px",
                        opacity: "0.8"
                    }}>
                        {documentTitle.length}/{maxTitleLength} characters. 
                        Only letters, numbers, spaces, periods, dashes, and underscores allowed.
                    </p>
                </div>

                {/* File upload area */}
                <div 
                    style={{ 
                        border: '2px dashed var(--bb-text)', 
                        borderRadius: '8px', 
                        padding: '40px', 
                        textAlign: 'center', 
                        marginTop: '20px',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        transition: 'all 0.3s ease'
                    }}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                >
                    <p style={{ marginBottom: "15px" }}>
                        {selectedFile ? `Selected: ${selectedFile.name}` : "Drag and drop your HTML or Markdown file here, or click to browse"}
                    </p>
                    <input
                        type="file"
                        accept=".html,text/html,.md,.markdown,.mdown,.mkd,text/markdown"
                        onChange={handleFileChange}
                        style={{ 
                            marginTop: '10px',
                            padding: '8px',
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            border: '1px solid var(--bb-text)',
                            borderRadius: '4px',
                            width: '100%',
                            maxWidth: '100%',
                            boxSizing: 'border-box'
                        }} 
                    />
                    {selectedFile && (
                        <p style={{
                            marginTop: "10px",
                            color: "green",
                            fontWeight: "bold",
                            fontSize: "14px"
                        }}>
                            ✓ File selected successfully
                        </p>
                    )}
                </div>

                {/* Image attachments section */}
                <div style={{ marginTop: "25px" }}>
                    <label style={{ 
                        display: "block", 
                        marginBottom: "10px", 
                        fontWeight: "bold",
                        fontSize: "16px"
                    }}>
                        Image Attachments (Optional):
                    </label>
                    <p className="small" style={{ marginBottom: "15px" }}>
                        If your HTML document references images, upload them here. We'll try to match them automatically based on filename.
                    </p>
                    
                    <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleImageUpload}
                        style={{
                            width: "100%",
                            padding: "8px",
                            backgroundColor: "rgba(255, 255, 255, 0.9)",
                            border: "1px solid var(--bb-text)",
                            borderRadius: "4px",
                            boxSizing: "border-box"
                        }}
                    />
                    
                    {imageAttachments.length > 0 && (
                        <div style={{ marginTop: "15px" }}>
                            <p style={{ fontWeight: "bold", marginBottom: "10px" }}>
                                Uploaded Images ({imageAttachments.length}):
                            </p>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                                {imageAttachments.map((image, index) => (
                                    <div 
                                        key={index} 
                                        style={{ 
                                            position: "relative",
                                            display: "inline-block",
                                            padding: "8px",
                                            backgroundColor: "rgba(255, 255, 255, 0.8)",
                                            borderRadius: "4px",
                                            border: "1px solid var(--bb-text)"
                                        }}
                                    >
                                        <span style={{ fontSize: "12px" }}>{image.name}</span>
                                        <button
                                            onClick={() => removeImageAttachment(index)}
                                            style={{
                                                position: "absolute",
                                                top: "-5px",
                                                right: "-5px",
                                                backgroundColor: "#ff4444",
                                                color: "white",
                                                width: "20px",
                                                height: "20px",
                                                border: "none",
                                                borderRadius: "3px",
                                                fontSize: "12px",
                                                cursor: "pointer"
                                            }}
                                            title="Remove image"
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div style={{ marginTop: "25px" }}>
                    <p className="medium">
                        Importing a <strong>Markdown (.md)</strong> file? Just select it above — we'll convert
                        headings, lists, tables, task lists, code blocks, and other Markdown formatting automatically.
                        You'll be able to review everything before finalizing.
                    </p>
                    <p className="medium">
                        As we improve the import feature, we'll add instructions for exporting documents from other platforms.
                        For now, <strong>here are instructions below for exporting/importing documents from Confluence</strong>.
                    </p>
                    <ol>
                        <li>
                            Navigate to your space in confluence. You should see your space name, "All Content", and 
                            "Space Settings" on the top left.
                        </li>
                        <li>
                            Click on "Space Settings".
                        </li>
                        <li>
                            Under "General", click on "Export space".
                        </li>
                        <li>
                            Be sure to choose the HTML format option, choose whether you want to export all content or select content, and 
                            then click "Export Space".
                        </li>
                        <li>
                            You should now have a ZIP file downloaded to your computer. Extract this ZIP file to access the HTML files.
                        </li>
                        <li>
                            Open the extracted folder and locate the HTML files. Upload the specific HTML file you want above.
                        </li>
                        <li>
                            If your HTML file references images, you can also upload those images in the section above. <i>You can find the 
                            images referenced in attachments directory of the extracted folder</i>.
                        </li>
                    </ol>
                </div>
            </div>

            {/* Back Arrow */}
            <div 
                className="ImportDocument__arrow ImportDocument__arrow--back" 
                onClick={handleBackClick}
                title="Go back"
                aria-label="Go back to previous step"
            >
                ❮
            </div>

            {/* Forward Arrow */}
            <div 
                className="ImportDocument__arrow ImportDocument__arrow--forward" 
                onClick={handleNextClick}
                title="Continue to next step"
                aria-label="Continue to next step"
            >
                ❯
            </div>
            {enhancedRightClickEnabled && ContextMenuComponent}
        </div>
    );
}

export default UploadWorkspaceDocument;
